import React, { ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "./button";
import { logger } from "@/lib/logger";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component for catching React errors
 * Displays a user-friendly error message instead of a blank page
 * Logs errors for monitoring and debugging
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for monitoring
    logger.error("Error Boundary caught an error", error, {
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="bg-card border border-destructive rounded-lg p-8 max-w-md text-center shadow-lg">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Something Went Wrong
              </h1>
              <p className="text-muted-foreground mb-6">
                We encountered an unexpected error. Please try again.
              </p>
              {process.env.NODE_ENV === "development" && this.state.error && (
                <details className="mb-6 text-left bg-muted p-3 rounded text-xs">
                  <summary className="cursor-pointer font-mono font-bold mb-2">
                    Error Details
                  </summary>
                  <pre className="overflow-auto text-destructive font-mono">
                    {this.state.error.toString()}
                  </pre>
                </details>
              )}
              <Button
                onClick={this.handleReset}
                variant="default"
                className="w-full"
              >
                Try Again
              </Button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
